import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

// Local cache key
const FEED_PROJECTS_CACHE_KEY = 'feed_projects_cache';

export const hydrateFeedProjectsFromCache = createAsyncThunk(
  'feedProjects/hydrateFromCache',
  async () => {
    try {
      const raw = localStorage.getItem(FEED_PROJECTS_CACHE_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr)
        ? arr.map(p => ({
          ...p,
          createdAt: p?.createdAt ? new Date(p.createdAt) : null,
        }))
        : [];
    } catch {
      return [];
    }
  }
);

export const setupFeedProjectsListener = createAsyncThunk(
  'feedProjects/setupListener',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const q = query(
        collection(db, 'projects'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      return await new Promise((resolve, reject) => {
        let firstResolved = false;
        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            try {
              const list = snapshot.docs.map(d => {
                const data = d.data();
                const createdAt = (data?.createdAt && typeof data.createdAt.toDate === 'function')
                  ? data.createdAt.toDate()
                  : (data?.createdAt ? new Date(data.createdAt) : null);
                return {
                  id: d.id,
                  ...data,
                  createdAt,
                };
              });

              // Cache compact form for instant hydration
              try {
                const toCache = list.slice(0, 20).map(p => ({
                  ...p,
                  createdAt: p?.createdAt instanceof Date ? p.createdAt.toISOString() : p?.createdAt || null,
                }));
                localStorage.setItem(FEED_PROJECTS_CACHE_KEY, JSON.stringify(toCache));
              } catch { }

              const prev = getState()?.feedProjects?.items || [];
              const sameLength = prev.length === list.length;
              let sameIds = sameLength;
              if (sameLength) {
                for (let i = 0; i < prev.length; i++) {
                  if (prev[i]?.id !== list[i]?.id) { sameIds = false; break; }
                }
              }
              if (!(sameLength && sameIds)) {
                dispatch(setItems(list));
              }

              if (!firstResolved) { firstResolved = true; resolve(unsubscribe); }
            } catch (e) {
              if (!firstResolved) reject(e);
            }
          },
          (error) => {
            if (!firstResolved) reject(error);
          }
        );
      });
    } catch (e) {
      return rejectWithValue(e?.message || 'Failed to setup projects listener');
    }
  }
);

const initialState = {
  items: [],
  listener: null,
  loading: false,
  error: null,
  ready: false,
};

const feedProjectsSlice = createSlice({
  name: 'feedProjects',
  initialState,
  reducers: {
    setItems: (state, action) => {
      state.items = action.payload;
      state.ready = true;
    },
    cleanupFeedProjectsListener: (state) => {
      if (state.listener) {
        try { state.listener(); } catch { }
        state.listener = null;
      }
    },
    clearFeedProjectsState: (state) => {
      state.items = [];
      state.error = null;
      state.ready = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateFeedProjectsFromCache.fulfilled, (state, action) => {
        if (Array.isArray(action.payload) && action.payload.length > 0 && state.items.length === 0) {
          state.items = action.payload;
        }
      })
      .addCase(setupFeedProjectsListener.pending, (state) => {
        if (state.listener) {
          try { state.listener(); } catch { }
          state.listener = null;
        }
        state.loading = true;
      })
      .addCase(setupFeedProjectsListener.fulfilled, (state, action) => {
        state.listener = action.payload; // unsubscribe fn
        state.loading = false;
        state.ready = true;
      })
      .addCase(setupFeedProjectsListener.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to start projects listener';
      });
  }
});

export const { setItems, cleanupFeedProjectsListener, clearFeedProjectsState } = feedProjectsSlice.actions;

// Thunk to refresh the projects feed (cleanup and re-setup listener)
export const refreshFeedProjectsListener = () => (dispatch, getState) => {
  // First cleanup existing listener
  dispatch(cleanupFeedProjectsListener());
  // Clear cache to force fresh data
  try {
    localStorage.removeItem(FEED_PROJECTS_CACHE_KEY);
  } catch { }
  // Re-setup listener
  return dispatch(setupFeedProjectsListener());
};

export default feedProjectsSlice.reducer;

